/**
 * A small cache so moving around the app — and coming back to it — feels
 * instant instead of re-loading every time.
 *
 * Pages seed their state from here and render immediately, then refresh in
 * the background. Reads/writes go through an in-memory Map first (fastest,
 * used while the tab stays open) backed by sessionStorage (survives a
 * reload or reopening the tab). sessionStorage is scoped to the browser
 * session — it's gone the moment the browser is closed, and cacheClear()
 * wipes it immediately on sign-out — so nothing lingers on a shared
 * computer past that.
 *
 * Keys are scoped by client (tenantId, or "owner" for the owner console),
 * so switching client — or a different owner signing in on the same
 * browser — never shows the previous account's figures.
 */
const store = new Map();

// Every sessionStorage key this module writes carries this prefix, so
// cacheClear() can find and remove exactly (and only) our own entries.
const PREFIX = "fh_cache:";

function readSession(key) {
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    return raw == null ? undefined : JSON.parse(raw);
  } catch {
    // Private-browsing storage restrictions, corrupted JSON, a full quota —
    // any of these just mean "no persisted value," never a crash.
    return undefined;
  }
}

function writeSession(key, value) {
  try {
    sessionStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota exceeded or storage unavailable — the in-memory Map still has
    // it for this tab's lifetime, which is the important part.
  }
}

export function cacheKey(name, tenantId) {
  return `${tenantId || "none"}:${name}`;
}

export function cacheGet(key) {
  if (store.has(key)) return store.get(key);
  const fromSession = readSession(key);
  if (fromSession !== undefined) store.set(key, fromSession);
  return fromSession;
}

export function cacheSet(key, value) {
  store.set(key, value);
  writeSession(key, value);
  return value;
}

/** Called on sign-out so the next person doesn't inherit anything. */
export function cacheClear() {
  store.clear();
  try {
    const toRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // No sessionStorage access — nothing to clear there.
  }
}
